import type {
  AccountRegistration,
  LoginNewsPayload,
  LoginPresentationTextPayload,
} from '../gateway/contracts';
import {
  type LegacyStartupAssetLoader,
  type LegacyStartupDownloadState,
  sharedLegacyStartupAssetLoader,
} from '../app/LegacyStartupAssetLoader';
import type { LegacyLoginRejection } from '../app/LegacyLoginRejection';
import { AccountCreationView, type AccountCreationOutcome } from './AccountCreationView';
import { LegacyLoginControlPanel } from './LegacyLoginControlPanel';
import { PublicCameraScene } from '../world/PublicCameraScene';

const LEGACY_DOWNLOAD_FILL_WIDTH_PERCENT = (264 / 278) * 100;
const ENGLISH_LOGIN_PRESENTATION: LoginPresentationTextPayload = {
  usernameLabel: 'Username',
  passwordLabel: 'Password',
  controlPanelLabel: 'Need support?',
  rememberMeLabel: 'Remember me',
  latestNewsLabel: 'Latest news',
  createAccountLabel: 'Create your account here!',
  startLabel: 'Start',
  downloadingUpdatesLabel: 'Downloading Updates...',
  downloadFinishedLabel: 'Download Finished',
};

export type LoginCallbacks = Readonly<{
  login: (username: string, password: string) => void;
  createAccount: (registration: AccountRegistration) => Promise<AccountCreationOutcome>;
  loadNews: (refresh?: boolean) => Promise<LoginNewsPayload>;
}>;

export class LoginView {
  readonly element: HTMLElement;
  private readonly loginForm: HTMLFormElement;
  private readonly backdrop: HTMLImageElement;
  private readonly controlPanelLauncher: HTMLButtonElement;
  private readonly usernameLabel: HTMLLabelElement;
  private readonly passwordLabel: HTMLLabelElement;
  private readonly username: HTMLInputElement;
  private readonly password: HTMLInputElement;
  private readonly rememberUsername: HTMLInputElement;
  private readonly rememberUsernameLabel: HTMLElement;
  private readonly loginButton: HTMLButtonElement;
  private readonly createButton: HTMLButtonElement;
  private readonly status: HTMLElement;
  private readonly download: HTMLElement;
  private readonly downloadFill: HTMLElement;
  private readonly downloadText: HTMLElement;
  private readonly downloadRetry: HTMLButtonElement;
  private readonly rejectionDialog: HTMLDialogElement;
  private readonly newsBody: HTMLElement;
  private readonly newsHeading: HTMLHeadingElement;
  private readonly newsImage: HTMLImageElement;
  private readonly publicCamera: PublicCameraScene;
  private readonly accountCreator: AccountCreationView;
  private readonly controlPanel: LegacyLoginControlPanel;
  private readonly unsubscribeStartupAssets: () => void;
  private startupAssetsReady = false;
  private startupPhase: LegacyStartupDownloadState['phase'] = 'loading';
  private loginBusy = false;
  private disposed = false;
  private newsRefreshTimer: ReturnType<typeof setTimeout> | undefined;
  private presentation = ENGLISH_LOGIN_PRESENTATION;

  constructor(
    callbacks: LoginCallbacks,
    initialUsername = '',
    message = '',
    initialPassword = '',
    private readonly startupAssets: LegacyStartupAssetLoader = sharedLegacyStartupAssetLoader,
    rejection?: LegacyLoginRejection,
  ) {
    this.element = document.createElement('main');
    this.element.className = 'login-shell';
    this.element.innerHTML = `
      <div class="login-stage">
      <section class="legacy-login" aria-labelledby="login-heading">
        <img class="legacy-login__art" src="/legacy/startup/loginbackdrop_eng.jpg" alt="" />
        <canvas class="public-camera" aria-label="Live public village camera"></canvas>
        <img class="public-camera__logo" src="/camera-drone/nord-logo.png" alt="Nord" />
        <button class="legacy-button login-control-panel__launcher" type="button">Need support?</button>
        <form class="login-form">
          <h1 id="login-heading">Log in</h1>
          <div class="login-download" data-state="loading" role="status" aria-live="polite">
            <span class="login-download__fill" aria-hidden="true"></span>
            <img src="/legacy/startup/loadbar_overlay.png" alt="" />
            <span class="login-download__text">Downloading Updates</span>
            <button class="login-download__retry" type="button" hidden>Retry</button>
          </div>
        <label for="username">Username:</label>
          <input id="username" name="username" autocomplete="username" maxlength="64" />
          <label for="password">Password:</label>
          <input id="password" name="password" type="password" autocomplete="current-password" maxlength="256" />
          <button class="legacy-button login-form__submit" type="submit">Start</button>
          <label class="login-form__remember"><input name="rememberUsername" type="checkbox" /> <span>Remember me</span></label>
          <button class="text-button login-form__create" type="button">Create your account here!</button>
          <p class="login-form__status" role="status" aria-live="polite"></p>
        </form>
        <section class="login-news" aria-label="News">
          <img src="/legacy/startup/accountbg.png" alt="" />
          <div class="login-news__copy">
            <h2>Latest news</h2>
            <div class="login-news__body"></div>
          </div>
        </section>
      </section>
      </div>
      <dialog class="login-rejection-dialog" aria-label="Nord login message">
        <h2 class="login-rejection-dialog__title"></h2>
        <p class="login-rejection-dialog__message"></p>
        <form method="dialog">
          <button type="submit" value="ok">OK</button>
        </form>
      </dialog>
    `;
    this.loginForm = requiredElement(this.element, '.login-form', HTMLFormElement);
    this.backdrop = requiredElement(this.element, '.legacy-login__art', HTMLImageElement);
    this.controlPanelLauncher = requiredElement(
      this.element,
      '.login-control-panel__launcher',
      HTMLButtonElement,
    );
    this.usernameLabel = requiredElement(this.element, 'label[for="username"]', HTMLLabelElement);
    this.passwordLabel = requiredElement(this.element, 'label[for="password"]', HTMLLabelElement);
    this.username = requiredElement(this.element, '#username', HTMLInputElement);
    this.password = requiredElement(this.element, '#password', HTMLInputElement);
    this.rememberUsername = requiredElement(
      this.element,
      '[name="rememberUsername"]',
      HTMLInputElement,
    );
    this.rememberUsernameLabel = requiredElement(
      this.element,
      '.login-form__remember span',
      HTMLElement,
    );
    this.loginButton = requiredElement(this.element, '.login-form__submit', HTMLButtonElement);
    this.createButton = requiredElement(this.element, '.login-form__create', HTMLButtonElement);
    this.status = requiredElement(this.element, '.login-form__status', HTMLElement);
    this.download = requiredElement(this.element, '.login-download', HTMLElement);
    this.downloadFill = requiredElement(this.download, '.login-download__fill', HTMLElement);
    this.downloadText = requiredElement(this.download, '.login-download__text', HTMLElement);
    this.downloadRetry = requiredElement(
      this.download,
      '.login-download__retry',
      HTMLButtonElement,
    );
    this.rejectionDialog = requiredElement(
      this.element,
      '.login-rejection-dialog',
      HTMLDialogElement,
    );
    this.newsBody = requiredElement(this.element, '.login-news__body', HTMLElement);
    this.newsHeading = requiredElement(this.element, '.login-news__copy h2', HTMLHeadingElement);
    this.newsImage = requiredElement(this.element, '.login-news > img', HTMLImageElement);
    const rememberedUsername = readRememberedUsername();
    this.username.value = initialUsername.length > 0 ? initialUsername : rememberedUsername;
    this.password.value = initialPassword;
    this.rememberUsername.checked =
      initialPassword.length > 0 ? true : readRememberUsernameEnabled();
    this.createButton.hidden = initialPassword.length > 0;
    this.status.textContent = message;

    this.publicCamera = new PublicCameraScene(
      requiredElement(this.element, '.public-camera', HTMLCanvasElement),
    );
    void this.publicCamera.start();
    this.accountCreator = new AccountCreationView({
      cancel: () => {
        this.publicCamera.setRegistrationOpen(false);
        this.focus();
      },
      create: (registration) => callbacks.createAccount(registration),
    });
    this.controlPanel = new LegacyLoginControlPanel();
    this.element.append(this.controlPanel.element);
    requiredElement(this.element, '.legacy-login', HTMLElement).append(this.accountCreator.element);
    this.loginForm.addEventListener('submit', (event) => {
      event.preventDefault();
      if (!this.startupAssetsReady || this.loginBusy) return;
      if (!this.loginForm.reportValidity()) return;
      writeRememberedUsername(
        this.rememberUsername.checked,
        this.rememberUsername.checked ? this.username.value : '',
      );
      callbacks.login(this.username.value, this.password.value);
    });
    this.createButton.addEventListener('click', () => {
      this.publicCamera.setRegistrationOpen(true);
      this.accountCreator.open();
    });
    this.controlPanelLauncher.addEventListener('click', () =>
      this.controlPanel.open(this.username.value.trim()),
    );
    this.downloadRetry.addEventListener('click', () => void this.startupAssets.retry());
    this.unsubscribeStartupAssets = this.startupAssets.subscribe((state) =>
      this.applyStartupDownloadState(state),
    );
    const refreshNews = (refresh = false): void => {
      void callbacks
        .loadNews(refresh)
        .then((news) => {
          if (this.disposed) return;
          this.applyLoginPresentation(news);
          this.newsBody.replaceChildren(
            ...news.paragraphs.map((paragraph) => {
              const element = document.createElement('p');
              element.textContent = paragraph;
              return element;
            }),
          );
          this.scheduleNewsTextFit();
          if (news.refreshAfterMs !== undefined) {
            this.newsRefreshTimer = setTimeout(
              () => refreshNews(true),
              Math.max(1000, Math.min(news.refreshAfterMs, 90_000_000)),
            );
          }
        })
        .catch(() => {
          // Keep the last news visible and retry temporary feed failures.
          if (!this.disposed) this.newsRefreshTimer = setTimeout(() => refreshNews(true), 30_000);
        });
    };
    refreshNews();
    void this.startupAssets.start();
    if (rejection?.modal !== undefined) {
      const modal = rejection.modal;
      const title = requiredElement(
        this.rejectionDialog,
        '.login-rejection-dialog__title',
        HTMLHeadingElement,
      );
      const modalMessage = requiredElement(
        this.rejectionDialog,
        '.login-rejection-dialog__message',
        HTMLParagraphElement,
      );
      title.textContent = modal.title ?? '';
      title.hidden = modal.title === undefined;
      modalMessage.textContent = modal.message;
      this.rejectionDialog.addEventListener('close', () => {
        if (!this.disposed && modal.reloadOnDismiss) globalThis.location.reload();
      });
      queueMicrotask(() => {
        if (!this.disposed && this.rejectionDialog.isConnected) {
          this.rejectionDialog.showModal();
          requiredElement(this.rejectionDialog, 'button', HTMLButtonElement).focus();
        }
      });
    }
  }

  private scheduleNewsTextFit(): void {
    const fit = (): void => {
      if (this.disposed) return;
      const copy = requiredElement(this.element, '.login-news__copy', HTMLElement);
      if (copy.clientHeight === 0) return;
      for (let sourceFontSize = 24; sourceFontSize >= 4; sourceFontSize -= 0.5) {
        copy.style.setProperty('--login-news-font-size', `${(sourceFontSize / 7.6).toFixed(6)}cqw`);
        copy.dataset.sourceFontSize = String(sourceFontSize);
        if (copy.scrollHeight <= copy.clientHeight) return;
      }
    };
    requestAnimationFrame(fit);
    void document.fonts.ready.then(fit);
  }

  setBusy(busy: boolean, status = ''): void {
    this.loginBusy = busy;
    this.username.disabled = busy;
    this.password.disabled = busy;
    this.rememberUsername.disabled = busy;
    this.loginButton.disabled = busy || !this.startupAssetsReady;
    this.createButton.disabled = busy;
    this.loginButton.textContent = busy ? 'Connecting…' : this.presentation.startLabel;
    if (status.length > 0) this.status.textContent = status;
  }

  focus(): void {
    (this.username.value.length === 0 ? this.username : this.password).focus();
  }

  getE2ePublicCameraNobos(): readonly Readonly<{
    noboType: number;
    worldX: number;
    worldZ: number;
  }>[] {
    return this.publicCamera.e2eNoboPresentations();
  }

  dispose(): void {
    this.disposed = true;
    clearTimeout(this.newsRefreshTimer);
    this.publicCamera.dispose();
    this.unsubscribeStartupAssets();
    this.controlPanel.close();
    if (this.rejectionDialog.open) this.rejectionDialog.close();
  }

  private applyStartupDownloadState(state: LegacyStartupDownloadState): void {
    this.startupPhase = state.phase;
    this.startupAssetsReady = state.phase === 'ready';
    this.download.dataset.state = state.phase;
    this.downloadFill.style.width = `${String(
      (state.percent * LEGACY_DOWNLOAD_FILL_WIDTH_PERCENT) / 100,
    )}%`;
    this.downloadText.textContent =
      state.phase === 'ready'
        ? this.presentation.downloadFinishedLabel
        : state.phase === 'failed'
          ? 'Download Failed'
          : this.presentation.downloadingUpdatesLabel;
    this.downloadRetry.hidden = state.phase !== 'failed';
    this.loginButton.disabled = this.loginBusy || !this.startupAssetsReady;
  }

  private applyLoginPresentation(news: LoginNewsPayload): void {
    this.presentation = news.texts;
    this.accountCreator.applyPresentation(news.accountCreation);
    this.backdrop.src = news.backdropAsset;
    this.controlPanelLauncher.textContent = news.texts.controlPanelLabel;
    this.usernameLabel.textContent = `${news.texts.usernameLabel}:`;
    this.passwordLabel.textContent = `${news.texts.passwordLabel}:`;
    this.rememberUsernameLabel.textContent = news.texts.rememberMeLabel;
    this.createButton.textContent = news.texts.createAccountLabel;
    this.newsHeading.textContent = news.texts.latestNewsLabel;
    this.newsImage.src = news.imageAsset;
    if (!this.loginBusy) this.loginButton.textContent = news.texts.startLabel;
    this.downloadText.textContent =
      this.startupPhase === 'ready'
        ? news.texts.downloadFinishedLabel
        : this.startupPhase === 'failed'
          ? 'Download Failed'
          : news.texts.downloadingUpdatesLabel;
  }
}

const REMEMBERED_USERNAME_KEY = 'nord.rememberedUsername.v1';
const REMEMBER_USERNAME_ENABLED_KEY = 'nord.rememberUsernameEnabled.v1';

function readRememberedUsername(): string {
  try {
    return localStorage.getItem(REMEMBERED_USERNAME_KEY)?.slice(0, 64) ?? '';
  } catch {
    return '';
  }
}

function readRememberUsernameEnabled(): boolean {
  try {
    return localStorage.getItem(REMEMBER_USERNAME_ENABLED_KEY) !== '0';
  } catch {
    return true;
  }
}

function writeRememberedUsername(enabled: boolean, username: string): void {
  try {
    localStorage.setItem(REMEMBER_USERNAME_ENABLED_KEY, enabled ? '1' : '0');
    if (username.length === 0) localStorage.removeItem(REMEMBERED_USERNAME_KEY);
    else localStorage.setItem(REMEMBERED_USERNAME_KEY, username.slice(0, 64));
  } catch {
    // Private/sandboxed contexts may reject storage; login itself remains available.
  }
}

function requiredElement<T extends Element>(
  root: ParentNode,
  selector: string,
  constructor: new () => T,
): T {
  const element = root.querySelector(selector);
  if (!(element instanceof constructor)) throw new Error(`Missing UI element: ${selector}`);
  return element;
}
